import { useEffect, useState, useCallback } from 'react';
import {
  fetchAuthSession,
  signIn,
  signOut,
  signInWithRedirect,
  getCurrentUser,
  type AuthUser,
} from 'aws-amplify/auth';

export interface AuthState {
  user: AuthUser | null;
  isLoading: boolean;
  isAdmin: boolean;
  email: string;
  displayName: string;
}

export function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');

  const checkAuth = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (session.tokens) {
        const u = await getCurrentUser();
        setUser(u);
        const payload = session.tokens.idToken?.payload ?? {};
        const groups = (payload['cognito:groups'] as string[]) ?? [];
        setIsAdmin(groups.includes('homestock-admin'));
        setEmail(
          (payload['email'] as string) ??
          u.signInDetails?.loginId ??
          '',
        );
        setDisplayName((payload['name'] as string) ?? '');
      } else {
        setUser(null);
        setIsAdmin(false);
        setEmail('');
        setDisplayName('');
      }
    } catch {
      setUser(null);
      setIsAdmin(false);
      setEmail('');
      setDisplayName('');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return {
    user,
    isLoading,
    isAdmin,
    email,
    displayName,
    signInWithGoogle: () => signInWithRedirect({ provider: 'Google' }),
    signInWithFacebook: () => signInWithRedirect({ provider: 'Facebook' }),
    signInWithEmail: (e: string, password: string) =>
      signIn({ username: e, password }),
    signOut: async () => {
      await signOut();
      setUser(null);
      setIsAdmin(false);
      setEmail('');
      setDisplayName('');
    },
    refresh: checkAuth,
  };
}
